import React, { type HTMLAttributes } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { BreadcrumbsV2 } from '@uncinc/react-drupal-core';
import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';

import { Container, WysiwygField } from '@uncinc/uncinc-react-kitchen-sink';

import {
  ArticleMetadata,
  ButtonWrapper,
  ParagraphsRenderer,
  SearchMoreLikeThis,
  VariableRatiosPicture,
} from '../../../../components';
import useLayoutVariables from '../../../../hooks/useLayoutVariables';
import type { NodeArticle } from '../../../../types/uncinc_profile/nodes';

import './style.scss';


interface NodeArticlePageProps extends React.HTMLProps<HTMLAttributes<HTMLDivElement>> {
  entity: ImmutableEntity<NodeArticle>;
}

const NodeArticlePage: React.FC<NodeArticlePageProps> = ({ entity, className }) => {
  const {
    heroImageBreakpointAspectRatios,
    breadcrumbsColClasses,
    heroTextColClasses,
    heroImageColClasses,
  } = useLayoutVariables('article');

  const { t } = useTranslation();

  const node = entity.toJS();
  const nid = node.nid[0].value;
  const langcode = node.langcode[0].value;
  const articleType = node.field_article_type[0]?.entity.name[0].value;
  const title = node.title[0].value;
  const body = node.body[0]?.processed;
  const link = node.field_link[0];
  const linkTitle = link?.title;
  const linkUrl = link?.alias || link?.uri;
  const image = node.field_media_item[0];
  const publicationDate = node.field_publish_date?.[0]?.value || node.created[0]?.value;
  const readTime = node.node_read_time[0].value;
  const paragraphs = node.field_paragraphs;
  const classes = classNames('Node', 'NodeArticlePage', className, {
    [`NodeArticlePage--type--${String(articleType?.replace(' ', '-'))}`]: articleType !== undefined,
  });

  return (
    <article itemType="http://schema.org/WebPage" className={classes}>
      <div className="NodeArticlePage__hero">
        <Container>
          <div className="row">
            <div className={breadcrumbsColClasses}>
              <BreadcrumbsV2 />
            </div>
          </div>
          <div className="row">
            <div className={heroTextColClasses}>
              <div className="NodeArticlePage__hero__text-wrapper">
                <div className="NodeArticlePage__hero__text">
                  <h1 className="NodeArticlePage__title" itemProp="title">
                    {title}
                  </h1>

                  {body && (
                    <WysiwygField
                      className="NodeArticlePage__body body--large"
                      property="schema:text"
                      html={body}
                    />
                  )}

                  <ArticleMetadata className="NodeArticlePage__metadata">
                    {publicationDate && (
                      <>
                        <ArticleMetadata.PublicationDate publicationDate={new Date(publicationDate)} />
                        <span className="NodeArticlePage__metadata__separator">&bull;</span>
                      </>
                    )}

                    <ArticleMetadata.ReadTime readTime={readTime} />
                  </ArticleMetadata>

                  {linkUrl && (
                    <div className="NodeArticlePage__link">
                      <ButtonWrapper href={linkUrl}>
                        {linkTitle || t('general.read_more')}
                      </ButtonWrapper>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {image && (
              <div className={heroImageColClasses}>
                <div className="NodeArticlePage__hero__image-wrapper">
                  <div className="NodeArticlePage__hero__image">
                    <VariableRatiosPicture
                      image={image}
                      aspectRatios={heroImageBreakpointAspectRatios}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </Container>
      </div>

      <ParagraphsRenderer className="NodeArticlePage__paragraphs" paragraphs={paragraphs} />

      <div className="NodeArticlePage__related-content">
        <Container>
          <div className="row">
            <div className="col-12 col-xl-10 offset-xl-1">
              <SearchMoreLikeThis
                entityId={nid}
                langcode={langcode}
                contentTypes={['article']}
                title={t('content_types.article.related_content')}
              />
            </div>
          </div>
        </Container>
      </div>
    </article>
  );
};

export default NodeArticlePage;
